import {
  toCapitalizeCase,
  useLazyQuery,
  useTranslation,
} from '@gimlite/common';
import {
  AutoCompleteType,
  Button,
  Col,
  Form,
  Popover,
  Row,
  useCSV,
  WizardItemType,
  Zone,
} from '@gimlite/ui-v1';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Query, QueryContractsArgs } from '../../../../client/graphql';
import { DefaultCredentialsQrcodeWizard } from '../../../../common/defaultValue/wizard';
import { contractsGql } from '../../../../common/gql/contracts.gql';
import {
  convertCredentialsBackToFront,
  CredentialType,
} from '../../../../common/mapper/credential.mapper';
import { PopupInfo } from '../../../../components/popup-info/popup-info.component';
import { ContractWizardInfos } from '../../../../composition/wizard-infos/contract.wizard-infos';
import {
  ContractDetailsForm,
  ContractDetailsFormType,
} from '../../../../form/contract-create/contract-create.form';
import {
  ContractCSVParams,
  ContractsCreateFormType,
} from '../../../../form/contracts-create/contracts-create.form';
import { CreateNewSingleContractWizardContext } from '../../createNewSingleContract.wizard';

export const ParkerStep = ({
  context: {
    contractBase,
    contractDetails,
    product: { id: productId, credentials: productCredentials },
    credentials,
    contractSearchSelect,
    parkingId,
  },
  validate,
  submit,
}: WizardItemType.Config.Component<CreateNewSingleContractWizardContext>) => {
  const { t, lang }: any = useTranslation();

  const [currentSearchItems, setCurrentSearchItems] = useState<
    AutoCompleteType.Data.Item[]
  >([]);

  const [contractsCall, contractsQuery] = useLazyQuery<
    { contracts: Query['contracts'] },
    QueryContractsArgs
  >(contractsGql);

  const contracts = useMemo(() => {
    if (!contractsQuery.data) return null;

    return contractsQuery.data?.contracts;
  }, [contractsQuery.data?.contracts]);

  const searchContract = useCallback(
    async (content: string) => {
      const { data } = await contractsCall({
        variables: {
          // content,
          categories: ['PERIOD_SUBSCRIPTION', 'MOTORIST_POOL'],
          parkingId,
        },
      });

      if (data?.contracts?.list) {
        setCurrentSearchItems(() =>
          data.contracts.list.map(({ _id, name1, name2, ospContractId }) => ({
            label: (
              (`${name1 || ''} ${name2 || ''}`.trim() || t('unknown')) +
              ' ' +
              `[${ospContractId}]`
            ).trim(),
            value: _id,
          })),
        );
      }
    },
    [lang],
  );

  const selectContract = useCallback(
    async (item: ContractDetailsFormType.Data.CurrentSearchContractValue) => {
      if (item) {
        const findContract = contracts?.list?.find(
          (element) => element._id === item.value,
        );

        if (!findContract) return;

        const {
          _id,
          credentials: credentialNotFormatted,
          address,
          username,
          name1,
          name2,
          lang,
          qrcode,
          ospContractId,
          motoristId,
          gender,
          shortUid,
        } = findContract;

        submit({
          credentials: {
            actived: convertCredentialsBackToFront(
              credentialNotFormatted || [],
              qrcode,
            ).filter(({ name }) => productCredentials.includes(name)),
            desactived: credentials.desactived,
            context: 'wizardContractExist',
          },
        });

        submit({
          contractDetails: {
            _id,
            name1: name1 ? toCapitalizeCase(name1) : null,
            name2: name2 ? toCapitalizeCase(name2) : null,
            address1: address?.address1 || null,
            address2: address?.address2 || null,
            city: address?.city || null,
            zipcode: address?.zipcode || null,
            country: address?.country || null,
            username: username || null,
            lang: lang || null,
            contractId: ospContractId || null,
            motoristId: motoristId || null,
            gender: gender || null,
            shortUid: shortUid || null,
            inviteMotorist: false,
          },
        });
      }
    },
    [contracts],
  );

  useEffect(() => {
    validate(!!contractSearchSelect);
  }, [contractSearchSelect]);

  useEffect(() => {
    if (
      !!!credentials.actived.find(({ name }) => name === 'QRCODE') &&
      productCredentials.find((item) => item === 'QRCODE') &&
      lang
    ) {
      submit({
        addFakeQRCODE: true,
        credentials: {
          ...credentials,
          actived: [
            DefaultCredentialsQrcodeWizard({
              t,
              lang,
            }) as CredentialType.CredentialFromFront,
            ...credentials.actived,
          ],
        },
      });
    }
  }, [credentials.actived, lang]);

  const credentialsEdit = {
    RFID: productCredentials.includes('RFID'),
    PLATE: productCredentials.includes('PLATE'),
    TCS: productCredentials.includes('TCS'),
  };
  const CSVParams = ContractCSVParams(credentialsEdit);
  const { name1, name2, username } = contractDetails;
  const { CombinationValidNode } = useCSV<ContractsCreateFormType.Extra.CSV>({
    valid: CSVParams.valid,
    fields: CSVParams.fields.map(({ label, ...rest }) => ({
      ...rest,
      label: t(label),
    })),
    data: [
      {
        uid: !!credentials.actived.find(({ name }) => name === 'TCS')
          ? '123456789'
          : null,
        plate: credentials.actived.find(({ name }) => name === 'PLATE')
          ? '123456789'
          : null,
        visibleId: !!credentials.actived.find(({ name }) => name === 'TCS')
          ? '123456789'
          : null,
        firstName: name1,
        lastName: name2,
        email: username,
      },
    ],
  });

  return (
    <Form
      data={{ value: contractDetails }}
      config={{ validateOnChange: true }}
      handleEvent={{
        change: (value: any) => {
          submit({ contractDetails: { ...contractDetails, ...value } });
        },
        validate: (value: boolean) => {
          validate(value);
        },
      }}
    >
      <Zone
        config={{
          gap: {
            y: 1,
            x: 1,
          },
          zones: [['info', 'form']],
          rows: ['1fr'],
          columns: ['min-content', '1fr'],
        }}
      >
        <Zone.Area config={{ area: 'info' }}>
          <ContractWizardInfos
            data={{
              productName: productId ? t(`product-${productId}-name`) : null,
              APBMode: contractBase.APBMode,
              APBNext:
                contractBase.APBMode !== 'NONE' ? contractBase.APBNext : null,
              state: contractBase.state,
              startDate: contractBase.startDate,
              endDate: contractBase.endDate,
              description: contractBase.description,
              reference: contractBase.reference,
              articleCode: contractBase.articleCode,
            }}
          />
        </Zone.Area>

        <Zone.Area config={{ area: 'form' }}>
          <ContractDetailsForm
            handleEvent={{
              submitCredentials: (value) => {
                submit({ credentials: value });
              },
              submitContract: (value) => {
                submit({ contractDetails: value });
              },
              validate: () => {
                const activedCredentials = credentials.actived.reduce(
                  (acc, credential) => {
                    switch (credential.name) {
                      case 'PLATE':
                        return { ...acc, plate: true };
                      case 'RFID':
                        return {
                          ...acc,
                          uid: true,
                          visibleId: credential.visibleId ? true : false,
                        };
                      case 'QRCODE':
                      default:
                        return acc;
                    }
                  },
                  {},
                );

                const currentCombinaison = Object.entries({
                  ...contractDetails,
                  ...activedCredentials,
                }).reduce((acc: any, [key, value]) => {
                  return value ? [...acc, key] : acc;
                }, []);

                const credentialsEdit = {
                  RFID: productCredentials.includes('RFID'),
                  PLATE: productCredentials.includes('PLATE'),
                  TCS: productCredentials.includes('TCS'),
                };

                const valid = ContractCSVParams(credentialsEdit).valid.map(
                  (combinaison) =>
                    combinaison.map((item) =>
                      item === 'email'
                        ? 'username'
                        : item === 'firstName'
                          ? 'name1'
                          : item === 'lastName'
                            ? 'name2'
                            : item,
                    ),
                );

                const findValidCombinaison = valid.findIndex(
                  (combinaison) =>
                    combinaison.length === 0 ||
                    combinaison.every((item) =>
                      currentCombinaison.includes(item),
                    ),
                );

                validate(
                  findValidCombinaison !== -1
                    ? true
                    : t('watermelon-INVALID_COMBINAISON'),
                );
              },
            }}
            data={{
              credentials,
              contract: { ...contractDetails },
            }}
            config={{
              credentialsEdit: {
                RFID: productCredentials.includes('RFID'),
                PLATE: productCredentials.includes('PLATE'),
                TCS: productCredentials.includes('TCS'),
              },
              search: {
                submitSearchContractValue: (value) => {
                  submit({ contractSearchSelect: value });
                },
                searchContract: (value) => searchContract(value),
                clearSearchContract: () => setCurrentSearchItems([]),
                selectContract: (value) => {
                  if (value) selectContract(value);
                  submit({ contractSearchSelect: value });
                },
                currentSearchContractItems: currentSearchItems,
                currentSearchContractValue: contractSearchSelect,
              },
            }}
          />
        </Zone.Area>
        <Zone.Area config={{ area: 'empty' }}>
          <></>
        </Zone.Area>
        <Zone.Area config={{ area: 'csv' }}>
          <Col config={{ vertical: 'center', horizontal: 'start' }}>
            <Row config={{ horizontal: 'center' }}>
              <Popover
                data={
                  <PopupInfo
                    data={{
                      node: CombinationValidNode,
                    }}
                    config={{
                      title: t('contractFieldCombinations'),
                      description: [
                        {
                          key: 'required',
                          label: t('required'),
                          value: '*',
                          color: 'error',
                        },
                      ],
                    }}
                  />
                }
              >
                <Button
                  config={{
                    size: 'small',
                    mode: 'stroke',
                    text: t('validContractFieldsCombinations'),
                  }}
                ></Button>
              </Popover>
            </Row>
          </Col>
        </Zone.Area>
      </Zone>
    </Form>
  );
};
